/**
 * @file Provides Microsoft OneDrive uploading capability in the frontend.
 *
 * Directly tries Beethoven Uploads for OneDrive files
 *
 */

import { trackEvent } from '@@/bits/analytics'
import { customAjax } from '@@/bits/custom_ajax'
import { captureMessage } from '@@/bits/error_tracker'
import { cleanupFilename } from '@@/bits/file_handler'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { compactUploadOwnerParams, type UploadOwnerParams } from '@@/bits/uploader'
import type { MicrosoftOneDriveUploadResponse } from '@@/types'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { FetchOptionCredentials, FetchOptionMode } from '@padlet/fetch/src/types'
import EventEmitter from 'events'

const BEETHOVEN_API_VERSION = 2

const trackError = function (label: string, context: any = {}): void {
  trackEvent('Microsoft OneDrive Integration Uploads', 'Error', label)
  context.fileType = label
  captureMessage('UploadError', { context })
}

const trackSuccess = function (label: string): void {
  trackEvent('Microsoft OneDrive Integration Uploads', 'Upload succeeded', label)
}

type UploadAttributesFailure = {
  responseData: any
  textStatus?: string
}

class UploadMicrosoftOneDriveFileJob extends EventEmitter {
  private fileId: string | null = null
  private driveId: string | null = null
  private endpoint: string | null = null
  private accessToken: string | null = null
  private readonly fileName: string | null = null
  private readonly fileType: string | null = null
  private readonly fileSize: number | null = null
  private readonly ownerParams: UploadOwnerParams
  private uploadAttributes: Promise<any> | null = null

  constructor({
    fileName,
    fileType,
    fileSize,
    wallId,
    libraryId,
    tenantId,
    classroomKind,
    classroomId,
  }: {
    fileName: string | null
    fileType: string | null
    fileSize: number | null
  } & UploadOwnerParams) {
    super()
    this.fileName = fileName
    this.fileType = fileType
    this.fileSize = fileSize
    this.ownerParams = { wallId, libraryId, tenantId, classroomKind, classroomId }
  }

  async uploadMicrosoftOneDriveFile({
    fileId,
    driveId,
    endpoint,
    accessToken,
  }: {
    fileId: string | null
    driveId: string | null
    endpoint: string | null
    accessToken: string | null
  }): Promise<void> {
    this.fileId = fileId
    this.driveId = driveId
    this.endpoint = endpoint
    this.accessToken = accessToken

    if (this.fileId == null) {
      throw new Error('Valid fileId must be provided')
    }

    if (this.driveId == null) {
      throw new Error('Valid driveId must be provided')
    }

    if (this.endpoint == null) {
      throw new Error('Valid endpoint must be provided')
    }

    if (this.accessToken == null) {
      throw new Error('Valid accessToken must be provided')
    }

    await this.performMicrosoftOneDriveFileUpload()
  }

  async uploadMicrosoftOneDriveUrl({
    fileId,
    driveId,
    endpoint,
    accessToken,
  }: {
    fileId: string | null
    driveId: string | null
    endpoint: string | null
    accessToken: string | null
  }): Promise<void> {
    this.fileId = fileId
    this.driveId = driveId
    this.endpoint = endpoint
    this.accessToken = accessToken

    if (this.fileId == null) {
      throw new Error('Valid fileId must be provided')
    }

    if (this.driveId == null) {
      throw new Error('Valid driveId must be provided')
    }

    if (this.endpoint == null) {
      throw new Error('Valid endpoint must be provided')
    }

    if (this.accessToken == null) {
      throw new Error('Valid accessToken must be provided')
    }

    await this.performMicrosoftOneDriveUrlUpload()
  }

  // For a file upload, the URL the file will have once stored. The owner's region stores the file,
  // so ask that region's Beethoven, through the upload attributes the upload itself then uses.
  async expectedUploadUrl({
    fileId,
    driveId,
    endpoint,
    accessToken,
  }: {
    fileId: string
    driveId: string
    endpoint: string
    accessToken: string
  }): Promise<string> {
    const beethovenUploaderAttributes = await this.requestUploadAttributes()
    const expectedUrlEndpoint = new URL(beethovenUploaderAttributes.url)
    expectedUrlEndpoint.pathname = `/beethoven-v${BEETHOVEN_API_VERSION}/microsoft-onedrive-expected-url`
    const data = await fetchJson<{ url: string }>(expectedUrlEndpoint.toString(), {
      method: HTTPMethod.post,
      mode: FetchOptionMode.cors,
      credentials: FetchOptionCredentials.omit,
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: asciiSafeStringify({
        file_id: fileId,
        drive_id: driveId,
        endpoint,
        access_token: accessToken,
      }),
    })
    return data.url
  }

  async performMicrosoftOneDriveUrlUpload(): Promise<void> {
    const beethovenUploaderAttributes = await this.uploadAttributesOrEmitError()
    if (beethovenUploaderAttributes == null) return

    // The thumbnails are stored by the Beethoven that receives this request, so post to the
    // owner's region, which Rails picked, with its upload token.
    try {
      const response: MicrosoftOneDriveUploadResponse = await fetchJson(beethovenUploaderAttributes.url, {
        method: HTTPMethod.post,
        mode: FetchOptionMode.cors,
        credentials: FetchOptionCredentials.omit,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: asciiSafeStringify({
          microsoft_onedrive_url: true,
          file_id: this.fileId,
          drive_id: this.driveId,
          endpoint: this.endpoint,
          access_token: this.accessToken,
        }),
      })

      trackSuccess(this.fileType ?? 'unknown')
      this.emitDone(response)
    } catch (error) {
      if (error.status === 'abort') return
      trackError(this.fileType ?? 'unknown', {
        fileId: this.fileId,
        driveId: this.driveId,
        endpoint: this.endpoint,
        error,
      })
      this.emitError(error)
    }
  }

  async performMicrosoftOneDriveFileUpload(): Promise<void> {
    const beethovenUploaderAttributes = await this.uploadAttributesOrEmitError()
    if (beethovenUploaderAttributes == null) return
    await this.hitBeethovenCompletionUrl(beethovenUploaderAttributes)
  }

  // Makes a call to `uploads_controller`, to get a BeethovenUploader instance specific to Microsoft
  // OneDrive that will provide a url and postback url to hit. Rails picks the owner's region for the
  // url. One request per job, so the expected URL and the upload come from the same Beethoven.
  private requestUploadAttributes(): Promise<any> {
    if (this.uploadAttributes == null) {
      this.uploadAttributes = new Promise((resolve, reject) => {
        customAjax({
          url: '/media/upload_attributes.json',
          dataType: 'json',
          type: 'GET',
          cache: false,
          sendOauthToken: true,
          data: {
            unique: new Date(),
            // this is ultimately what tells the upload controller to use the Microsoft OneDrive Beethoven endpoint
            isMicrosoftOneDriveFile: true,
            ...compactUploadOwnerParams(this.ownerParams),
          },
          error: (responseData, textStatus) => {
            const failure: UploadAttributesFailure = { responseData, textStatus }
            reject(failure)
          },
          success: resolve,
        })
      })
    }
    return this.uploadAttributes
  }

  private async uploadAttributesOrEmitError(): Promise<any> {
    try {
      return await this.requestUploadAttributes()
    } catch (failure) {
      const { responseData, textStatus } = failure as UploadAttributesFailure
      if (textStatus === 'abort') return null
      trackError('startUploadAjax', {
        statusCode: responseData.status,
        readyState: responseData.readyState,
      })
      this.emitError(responseData as unknown as MicrosoftOneDriveUploadResponse)
      return null
    }
  }

  private async hitBeethovenCompletionUrl(beethovenUploaderAttributes: any): Promise<void> {
    // Disable as wel need this alias to call the emit functions
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this
    const fileUrl = beethovenUploaderAttributes.url
    const requestData = {}
    const fieldsOverrides = {
      transcode: true,
    }
    const beethovenCompletionAttributes = Object.assign({}, beethovenUploaderAttributes.fields, fieldsOverrides)

    Object.entries(beethovenCompletionAttributes).forEach((attribute) => {
      if (attribute[0] !== 'key') {
        requestData[attribute[0]] = attribute[1]
      }
    })

    try {
      // The upload URL can be another region's Beethoven: Rails picks the owner's region. Post to
      // it as given; the app's fetch wrapper would swap its host for the app's own.
      const response: MicrosoftOneDriveUploadResponse = await fetchJson(fileUrl, {
        method: HTTPMethod.post,
        mode: FetchOptionMode.cors,
        credentials: FetchOptionCredentials.omit,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: asciiSafeStringify({
          key: [beethovenUploaderAttributes.fields.key, cleanupFilename(this.fileName ?? 'untitled')].join('/'),
          file_size: this.fileSize?.toString(),
          file_id: this.fileId,
          drive_id: this.driveId,
          endpoint: this.endpoint,
          access_token: this.accessToken,
          title: this.fileName,
          url: fileUrl,
          ...requestData,
        }),
      })

      trackSuccess(this.fileType ?? 'unknown')
      self.emitDone(response)
    } catch (error) {
      if (error.status === 'abort') return
      trackError(this.fileType ?? 'unknown', {
        userId: beethovenCompletionAttributes.uploader_id,
        filename: this.fileName,
        fileSize: this.fileSize,
        maxFileSize: beethovenCompletionAttributes.max_file_size,
        error,
      })
      self.emitError(error)
    }
  }

  /*
   * Emits
   */
  private emitDone(doneObject: MicrosoftOneDriveUploadResponse): void {
    this.emit('done', doneObject)
  }

  private emitError(errorObject: MicrosoftOneDriveUploadResponse): void {
    this.emit('error', errorObject)
  }
}
export { UploadMicrosoftOneDriveFileJob }
